import type {
  AiTokenStatus,
  AiUsageTokenWithStats,
  ApplicationEnvironment,
  ApplicationWithStats,
  UpdateAiToken,
} from '@owl/shared';
import { useCallback, useEffect, useId, useState } from 'react';
import { useConfirm } from '../components/Confirm';
import type { ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { TokenRevealModal } from '../components/TokenRevealModal';
import { Icon } from '../components/icons';
import { EnvChip, StatusDot } from '../components/shared';
import { activateOnKey, isFromRowActions } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { timeAgo } from '../lib/time';
import { useAutoFocus } from '../lib/useAutoFocus';
import { useEscapeKey } from '../lib/useEscapeKey';
import { useLoader } from '../lib/useLoader';

function formatUsd(n: number): string {
  if (n === 0) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(2)}`;
}

export function AiTokens({ ctx }: { ctx: ShellCtx }) {
  const refreshTick = ctx.refreshTick;
  const env = ctx.environment;
  const [items, setItems] = useState<AiUsageTokenWithStats[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<{ token: string; name: string } | null>(null);
  const [editing, setEditing] = useState<AiUsageTokenWithStats | null>(null);
  const { push } = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try {
      const { items } = await api.listAiTokens({ environment: env });
      setItems(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [env]);

  useLoader(load, refreshTick);

  async function handleStatusChange(token: AiUsageTokenWithStats, status: AiTokenStatus) {
    try {
      await api.updateAiToken(token.id, { status });
      push(`${token.label} → ${status}`);
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Update failed');
    }
  }

  async function handleEdit(token: AiUsageTokenWithStats, patch: UpdateAiToken) {
    try {
      await api.updateAiToken(token.id, patch);
      push(`${token.label} updated`);
      setEditing(null);
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Update failed');
    }
  }

  async function handleDelete(token: AiUsageTokenWithStats) {
    const ok = await confirm({
      title: 'Delete AI token',
      sub: token.label,
      body: 'Existing events keep their app link, but ai_token_id becomes null.',
      confirmLabel: 'Delete token',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteAiToken(token.id);
      push(`${token.label} deleted`);
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Delete failed');
    }
  }

  async function handleRegenerate(token: AiUsageTokenWithStats) {
    const ok = await confirm({
      title: 'Regenerate AI token',
      sub: token.label,
      body: 'The current token stops working immediately. Any client still using it will start failing.',
      confirmLabel: 'Regenerate',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const { rawToken } = await api.regenerateAiToken(token.id);
      setRevealed({ token: rawToken, name: `${token.applicationName} · ${token.label}` });
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Regenerate failed');
    }
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">AI usage tokens</div>
          <div className="view-sub">
            {items?.length ?? 0} token{(items?.length ?? 0) === 1 ? '' : 's'} · separate Bearer
            credentials for /v1/ai-usage/ingest
          </div>
        </div>
        <button type="button" className="cta" onClick={() => setCreating(true)}>
          <Icon.Plus size={13} /> New AI token
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {items === null ? (
        <div className="empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          No AI tokens yet. Create one to start sending AI usage events to OWL.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Label</th>
                <th>Env</th>
                <th>Status</th>
                <th>Events 24h</th>
                <th>Cost 24h</th>
                <th>Last used</th>
                <th>Token</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr
                  key={t.id}
                  className="data-row"
                  onClick={(e) => {
                    if (isFromRowActions(e)) return;
                    ctx.navigate({ name: 'aiappdetail', applicationId: t.applicationId });
                  }}
                  onKeyDown={(e) =>
                    activateOnKey(e, () =>
                      ctx.navigate({ name: 'aiappdetail', applicationId: t.applicationId }),
                    )
                  }
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  title="Open AI app detail"
                >
                  <td>
                    <strong>{t.applicationName}</strong>{' '}
                    <span className="dim">
                      · {t.applicationProduct} / {t.applicationCompany}
                      {t.applicationInstance ? ` / ${t.applicationInstance}` : ''}
                    </span>
                  </td>
                  <td>{t.label}</td>
                  <td>
                    <EnvChip environment={t.applicationEnvironment} />
                  </td>
                  <td>
                    <StatusDot status={t.status} /> <span className="dim">{t.status}</span>
                  </td>
                  <td className="mono">{t.events24h}</td>
                  <td className="mono">{formatUsd(t.cost24hUsd)}</td>
                  <td className="dim">{timeAgo(t.lastUsedAt)}</td>
                  <td className="mono dim">{t.tokenPrefix}…</td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit"
                        onClick={() => setEditing(t)}
                      >
                        <Icon.Edit size={12} />
                      </button>
                      {t.status === 'active' ? (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Pause"
                          onClick={() => handleStatusChange(t, 'paused')}
                        >
                          <Icon.Pause size={12} />
                        </button>
                      ) : t.status === 'paused' ? (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Reactivate"
                          onClick={() => handleStatusChange(t, 'active')}
                        >
                          <Icon.Play size={12} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="icon-btn"
                        title="Regenerate token"
                        onClick={() => handleRegenerate(t)}
                      >
                        <Icon.Refresh size={12} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="Delete"
                        onClick={() => handleDelete(t)}
                      >
                        <Icon.Trash size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateAiTokenModal
          defaultEnvironment={env}
          onClose={() => setCreating(false)}
          onCreated={(name, token) => {
            setRevealed({ token, name });
            setCreating(false);
            load();
          }}
        />
      )}

      {revealed && (
        <TokenRevealModal
          token={revealed.token}
          applicationName={revealed.name}
          onClose={() => setRevealed(null)}
        />
      )}

      {editing && (
        <EditAiTokenModal
          token={editing}
          onClose={() => setEditing(null)}
          onSubmit={(patch) => handleEdit(editing, patch)}
        />
      )}
    </div>
  );
}

function EditAiTokenModal({
  token,
  onClose,
  onSubmit,
}: {
  token: AiUsageTokenWithStats;
  onClose: () => void;
  onSubmit: (patch: UpdateAiToken) => void | Promise<void>;
}) {
  useEscapeKey(onClose);
  const [label, setLabel] = useState(token.label);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFocusRef = useAutoFocus<HTMLInputElement>();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = label.trim();
    if (trimmed.length === 0) {
      setError('Label cannot be empty');
      return;
    }
    if (trimmed === token.label) {
      onClose();
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit({ label: trimmed });
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card">
        <div className="modal-head">
          <div className="modal-title">Edit AI token</div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="form-row">
            <span className="form-label">Label</span>
            <input
              className="form-input"
              ref={autoFocusRef}
              value={label}
              maxLength={64}
              onChange={(e) => setLabel(e.target.value)}
              required
            />
            <div className="hint">
              Application: <span className="dim">{token.applicationName}</span>
            </div>
          </label>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateAiTokenModal({
  defaultEnvironment,
  onClose,
  onCreated,
}: {
  defaultEnvironment: ApplicationEnvironment;
  onClose: () => void;
  onCreated: (name: string, token: string) => void;
}) {
  useEscapeKey(onClose);
  const [apps, setApps] = useState<ApplicationWithStats[] | null>(null);
  const [applicationId, setApplicationId] = useState<string>('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const appSelectId = useId();

  useEffect(() => {
    (async () => {
      try {
        const { items } = await api.listApplications(defaultEnvironment);
        setApps(items);
        // Seed the picker with the first application, but only while nothing is
        // chosen. Reading `applicationId` from state here rather than from the
        // closure keeps this out of the dependency list, so picking an
        // application cannot re-trigger the fetch.
        const first = items[0];
        if (first) setApplicationId((current) => current || first.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load applications');
      }
    })();
  }, [defaultEnvironment]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!applicationId) {
      setError('Pick an application');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { token, rawToken } = await api.createAiToken({ applicationId, label });
      const app = apps?.find((a) => a.id === token.applicationId);
      onCreated(`${app?.name ?? 'AI token'} · ${token.label}`, rawToken);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card">
        <div className="modal-head">
          <div className="modal-title">New AI token</div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          {/* The select only exists once applications have loaded, so the label is
              tied to it by id rather than by wrapping the branch. */}
          <div className="form-row">
            <label className="form-label" htmlFor={appSelectId}>
              Application
            </label>
            {apps === null ? (
              <div className="hint">Loading applications…</div>
            ) : apps.length === 0 ? (
              <div className="hint">No applications in this environment. Create one first.</div>
            ) : (
              <select
                id={appSelectId}
                className="form-input"
                value={applicationId}
                onChange={(e) => setApplicationId(e.target.value)}
                required
              >
                {apps.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <label className="form-row">
            <span className="form-label">Label</span>
            <input
              className="form-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Production key"
              maxLength={64}
              required
            />
            <div className="hint">
              Unique per application. Use it to distinguish keys per environment or rotation. A
              single token can report usage for any provider/model — the AI identity travels in the
              ingest payload.
            </div>
          </label>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="cta"
              disabled={busy || apps === null || apps.length === 0}
            >
              {busy ? 'Creating…' : 'Create AI token'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
